import * as _ from 'lodash-es';
import { useMemo } from 'react';
import useAllGroups from '../../groups/useAllGroups';
import useAllSlimPosts from '../../post/useAllSlimPosts';
import useAllTopics from '../../topic/useAllTopics';
import useAllUsers from '../../user/useAllUsers';
import type {
  GroupEntity,
  LocalEntitySearchConfig,
  PostEntity,
  TopicEntity,
  UserEntity,
} from './localEntitySearch';
import localEntitySearch from './localEntitySearch';

type FullGroupData = ReturnType<typeof useAllGroups>[number];
export type FullPostData = ReturnType<typeof useAllSlimPosts>[number];
type FullTopicData = ReturnType<typeof useAllTopics>[number];
type FullUserData = ReturnType<typeof useAllUsers>[number];

type DistributedOmit<T> = T extends undefined ? T : Omit<T, 'objects'>;

type OmitObjects<T extends { [key: string]: unknown }> = {
  [K in keyof T]: T extends object ? DistributedOmit<T[K]> : T;
};

export type UseEntitySearchConfig = OmitObjects<
  LocalEntitySearchConfig<
    FullGroupData,
    FullPostData,
    FullTopicData,
    FullUserData
  >
>;

export type UseEntitySearchGroupResult = GroupEntity<FullGroupData>;
export type UseEntitySearchPostResult = PostEntity<FullPostData>;
export type UseEntitySearchTopicResult = TopicEntity<FullTopicData>;
export type UseEntitySearchUserResult = UserEntity<FullUserData>;
export type UseEntitySearchResult =
  | UseEntitySearchGroupResult
  | UseEntitySearchPostResult
  | UseEntitySearchTopicResult
  | UseEntitySearchUserResult;

const useEntitySearch = (
  query: string,
  config: UseEntitySearchConfig,
  options?: Parameters<typeof localEntitySearch>[2],
): UseEntitySearchResult[] => {
  const allGroups = useAllGroups({
    skip: !config.group,
  });
  const allPosts = useAllSlimPosts();
  const allUsers = useAllUsers({
    includeDeactivated: config.user?.includeDeactivated,
  });

  const allTopics = useAllTopics({
    flatten: true,
  });

  return useMemo(() => {
    const objectsMap = {
      group: allGroups,
      post: allPosts,
      topic: allTopics,
      user: allUsers,
    } as const;

    const searchConfig = _.mapValues(
      config,
      (value, key: keyof typeof config) => {
        if (key === 'order') {
          return value;
        }

        const objects = objectsMap[key];

        return objects && { ...value, objects };
      },
    );

    return localEntitySearch<
      FullGroupData,
      FullPostData,
      FullTopicData,
      FullUserData
    >(query, searchConfig, options);
  }, [allGroups, allPosts, allTopics, allUsers, config, options, query]);
};

export default useEntitySearch;
